
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Kanban, 
  Plus, 
  MoreVertical, 
  Star,
  Users,
  Clock,
  Archive
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useBoardContext } from '@/contexts/BoardContext';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from '@/hooks/useScreenSize';
import { CreateBoardDialog } from '@/components/CreateBoardDialog';
import { formatDistanceToNow } from 'date-fns';

interface BoardGridProps {
  viewMode: 'grid' | 'list';
}

export const BoardGrid = ({ viewMode }: BoardGridProps) => {
  const { boards, allCards, allColumns, setCurrentBoardId } = useBoardContext();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useScreenSize();
  const [showCreateBoard, setShowCreateBoard] = useState(false);
  const [starredBoards, setStarredBoards] = useState<Set<string>>(new Set());

  const handleBoardSelect = (boardId: string) => {
    setCurrentBoardId(boardId);
    navigate('/');
  };

  const toggleStarred = (boardId: string) => {
    setStarredBoards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(boardId)) {
        newSet.delete(boardId);
      } else {
        newSet.add(boardId);
      }
      return newSet;
    });
  };

  const getBoardStats = (boardId: string) => {
    if (!allCards || !allColumns) return { totalCards: 0, completedCards: 0 };
    
    // Get columns for this board
    const boardColumns = allColumns.filter(column => column.board_id === boardId);
    const columnIds = boardColumns.map(column => column.id);
    
    // Get cards for these columns
    const boardCards = allCards.filter(card => columnIds.includes(card.column_id));
    const totalCards = boardCards.length;
    const completedCards = boardCards.filter(card => card.archived).length;
    
    return { totalCards, completedCards };
  };

  const getRecentActivity = (boardId: string) => {
    if (!allCards || !allColumns) return 'No activity';
    
    // Get columns for this board
    const boardColumns = allColumns.filter(column => column.board_id === boardId);
    const columnIds = boardColumns.map(column => column.id);
    
    // Get cards for these columns
    const boardCards = allCards.filter(card => columnIds.includes(card.column_id));
    
    const recentCard = boardCards
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())[0];
    
    return recentCard ? formatDistanceToNow(new Date(recentCard.updated_at), { addSuffix: true }) : 'No activity';
  };

  // Separate starred and regular boards
  const starred = boards?.filter(board => starredBoards.has(board.id)) || [];
  const regular = boards?.filter(board => !starredBoards.has(board.id)) || [];
  const sortedBoards = [...starred, ...regular];

  if (viewMode === 'list') {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Kanban className="h-5 w-5" />
            Your Boards
          </CardTitle>
          <Button onClick={() => setShowCreateBoard(true)} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            New Board
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="space-y-0">
            {sortedBoards.map((board) => {
              const stats = getBoardStats(board.id);
              const lastActivity = getRecentActivity(board.id);
              const isStarred = starredBoards.has(board.id);

              return (
                <div 
                  key={board.id}
                  className="flex items-center justify-between p-4 border-b border-border last:border-b-0 hover:bg-muted/50 cursor-pointer"
                  onClick={() => handleBoardSelect(board.id)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Kanban className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium truncate">{board.title}</h3>
                        {isStarred && <Star className="h-4 w-4 text-yellow-500 fill-current" />}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {stats.totalCards} cards • {lastActivity}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {stats.totalCards > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {Math.round((stats.completedCards / stats.totalCards) * 100)}% complete
                      </Badge>
                    )}
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => toggleStarred(board.id)}>
                          <Star className="h-4 w-4 mr-2" />
                          {isStarred ? 'Remove from Favorites' : 'Add to Favorites'}
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Users className="h-4 w-4 mr-2" />
                          Manage Members
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Archive className="h-4 w-4 mr-2" />
                          Archive Board
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
        <CreateBoardDialog open={showCreateBoard} onOpenChange={setShowCreateBoard} />
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Kanban className="h-5 w-5" />
          Your Boards
        </CardTitle>
        <Button onClick={() => setShowCreateBoard(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          New Board
        </Button>
      </CardHeader>
      <CardContent>
        <div className={`grid gap-4 ${
          isMobile 
            ? 'grid-cols-1' 
            : isTablet 
              ? 'grid-cols-2' 
              : 'grid-cols-2 xl:grid-cols-3'
        }`}>
          {sortedBoards.map((board) => {
            const stats = getBoardStats(board.id);
            const lastActivity = getRecentActivity(board.id);
            const isStarred = starredBoards.has(board.id);

            return (
              <Card 
                key={board.id} 
                className="cursor-pointer transition-all hover:shadow-md hover:scale-105"
                onClick={() => handleBoardSelect(board.id)}
              >
                <CardHeader className={`${isMobile ? 'pb-2' : 'pb-3'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className={`${isMobile ? 'text-base' : 'text-lg'} flex items-center gap-2`}>
                        <Kanban className="h-5 w-5" />
                        <span className="truncate">{board.title}</span>
                        {isStarred && <Star className="h-4 w-4 text-yellow-500 fill-current" />}
                      </CardTitle>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => toggleStarred(board.id)}>
                          <Star className="h-4 w-4 mr-2" />
                          {isStarred ? 'Remove from Favorites' : 'Add to Favorites'}
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Users className="h-4 w-4 mr-2" />
                          Manage Members
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Archive className="h-4 w-4 mr-2" />
                          Archive Board
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>{stats.totalCards} cards</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {lastActivity}
                      </span>
                    </div>
                    
                    {stats.totalCards > 0 && (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-muted-foreground">Progress</span>
                          <span className="text-xs font-medium">
                            {Math.round((stats.completedCards / stats.totalCards) * 100)}%
                          </span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all" 
                            style={{ width: `${(stats.completedCards / stats.totalCards) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </CardContent>
      <CreateBoardDialog open={showCreateBoard} onOpenChange={setShowCreateBoard} />
    </Card>
  );
};
